import { en, type MessageKey } from './en.ts';

/**
 * A deliberately tiny message-catalogue layer. No dependency, no ICU, no
 * pluralisation engine — the dashboard needs a place to hold its strings and a
 * function that resolves them, and a heavy i18n library would be more surface
 * than this earns today. `web/docs/i18n.md` is the reasoning and the path to
 * add a locale.
 *
 * Three properties are load-bearing:
 *
 * - **English is the default and the only shipped locale.** `catalogues` holds
 *   exactly `en`. This scaffold makes a second locale *possible*; it must never
 *   present non-English text as a real translation when none exists.
 * - **A miss is visible, never fatal.** Resolution falls back active-locale →
 *   `en` → the key string itself. A screen with an untranslated key shows the
 *   key, which is an obvious defect in review, rather than throwing in an
 *   operator's face mid-incident.
 * - **`{name}` interpolation only.** Enough for a count or a hash fragment;
 *   anything richer is a component's job, not the catalogue's.
 */

export type { MessageKey };

/**
 * The registry. Adding a locale is: write `web/src/i18n/<code>.ts` exporting a
 * `Partial<Record<MessageKey, string>>`, import it, add it here. Partial on
 * purpose — a locale may translate some keys and leave the rest to fall back to
 * English, so a half-finished catalogue never blanks the screen.
 *
 * It stays `{ en }` until a locale is genuinely, human-reviewed ready. See the
 * do-not-machine-translate rule in `web/docs/i18n.md`.
 */
const catalogues: Partial<Record<string, Partial<Record<MessageKey, string>>>> = { en };

let active = 'en';

/** The active locale code. Only 'en' resolves to a real catalogue today. */
export function locale(): string {
  return active;
}

/**
 * Switch the active locale. A code with no registered catalogue is allowed and
 * simply falls back to English for every key — so this can never surface a
 * language the product does not actually speak.
 */
export function setLocale(code: string): void {
  active = code;
}

/**
 * Resolve a message key to its string in the active locale, filling any
 * `{name}` placeholders from `params`.
 *
 * A key absent from both the active locale and `en` returns the key itself.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const table = catalogues[active] ?? en;
  const template = table[key] ?? en[key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}
